/*
 * Copyright (c) 2017 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschuetzt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulaessig. Dies gilt
 * insbesondere fuer Vervielfaeltigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht uebergeben ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 *
 */
import Raven = require('raven-js');
import AuthServiceRS from '../../authentication/service/AuthServiceRS.rest';
import ErrorService from '../../core/errors/service/ErrorService';
import {TSAuthEvent} from '../../models/enums/TSAuthEvent';
import {TSBegruendungsCode} from '../../models/enums/TSBegruendungsCode';
import {TSBemerkungScope} from '../../models/enums/TSBemerkungScope';
import {TSErfassungsArt} from '../../models/enums/TSErfassungsArt';
import {TSSchadenfallEvent} from '../../models/enums/TSSchadenfallEvent';
import {TSSchadenfallStatus} from '../../models/enums/TSSchadenfallStatus';
import TSAbstractBemerkungEntity from '../../models/TSAbstractBemerkungEntity';
import TSBankverbindung from '../../models/TSBankverbindung';
import TSBeitrag from '../../models/TSBeitrag';
import TSBenutzer from '../../models/TSBenutzer';
import TSBenutzerBemerkung from '../../models/TSBenutzerBemerkung';
import TSEigenleistung from '../../models/TSEigenleistung';
import TSFremdkosten from '../../models/TSFremdkosten';
import TSSchaden from '../../models/TSSchaden';
import TSSchadenfall from '../../models/TSSchadenfall';
import TSTransitionBemerkung from '../../models/TSTransitionBemerkung';
import TSVorleistung from '../../models/TSVorleistung';
import DVBernUtil from '../../utils/DVBernUtil';
import BeitragRS from './BeitragRS';
import BemerkungRS from './bemerkungRS.rest';
import BerechtigungsService from './BerechtigungsService';
import EigenleistungenRS from './EigenleistungenRS';
import FremdkostenRS from './FremdkostenRS';
import RoleUtilService from './RoleUtilService';
import SchadenfallRS from './schadenfallRS.rest';
import SchadenRS from './schadenRS.rest';
import VorleistungRS from './VorleistungenRS';
import ILogService = angular.ILogService;
import IPromise = angular.IPromise;
import IQService = angular.IQService;
import IRootScopeService = angular.IRootScopeService;

export default class SchadenfallModelManager {
    private schadenfall?: TSSchadenfall;

    static $inject = [
        'SchadenfallRS', 'SchadenRS', '$log', 'AuthServiceRS', 'ErrorService', '$q', 'CONSTANTS', '$rootScope',
        'RoleUtilService', 'FremdkostenRS', 'EigenleistungenRS', 'VorleistungenRS', 'BemerkungRS', 'BeitragRS', 'BerechtigungsService',
    ];

    /* @ngInject */
    constructor(private schadenfallRS: SchadenfallRS, private schadenRS: SchadenRS, private log: ILogService,
                private authServiceRS: AuthServiceRS,
                private errorService: ErrorService, private $q: IQService, private CONSTANTS: any,
                private $rootScope: IRootScopeService, private roleUtilService: RoleUtilService,
                private fremdkostenRS: FremdkostenRS, private eigenleistungenRS: EigenleistungenRS,
                private vorleistungenRS: VorleistungRS, private bemerkungRS: BemerkungRS,
                private beitragRS: BeitragRS,
                private berechtigungsService: BerechtigungsService,
    ) {

        $rootScope.$on(TSAuthEvent[TSAuthEvent.LOGOUT_SUCCESS], () => {
            this.setSchadenfall(undefined);
            this.log.debug('Cleared gesuch on logout');
        });
    }

    public initNewSchadenfall(): IPromise<TSSchadenfall> {
        this.schadenfall = new TSSchadenfall().init();
        if (this.authServiceRS.isOneOfRoles(this.roleUtilService.getGesuchstellerOnlyRoles())) {
            this.schadenfall.status = TSSchadenfallStatus.NEU;
            this.schadenfall.erfassungsart = TSErfassungsArt.ONLINE;
        } else if (this.authServiceRS.isOneOfRoles(this.roleUtilService.getFondssuisseRoles())) {
            this.schadenfall.status = TSSchadenfallStatus.NEU_PAPIER_FS;
            this.schadenfall.erfassungsart = TSErfassungsArt.PAPIER;
            this.schadenfall.gesuchsteller.finanzangaben!.zustimmungSteuerpruefung = true; // bei erstperson sind finanzangaben sicher da
            this.schadenfall.gesuchsteller.finanzangaben!.zustimmungPruefungFondsuisse = true;
        } else if (this.authServiceRS.isOneOfRoles(this.roleUtilService.getAnlaufstelleRoles())) {
            this.schadenfall.status = TSSchadenfallStatus.NEU_PAPIER_ANLAUFSTELLE;
            this.schadenfall.erfassungsart = TSErfassungsArt.PAPIER;
            this.schadenfall.gesuchsteller.finanzangaben!.zustimmungSteuerpruefung = true; // bei erstperson sind finanzangaben sicher da
            this.schadenfall.gesuchsteller.finanzangaben!.zustimmungPruefungFondsuisse = true;
        }
        return this.$q.when(this.schadenfall);
    }

    /**
     * Je nach dem welche Rolle der Benutzer hat, wird das Gesuch aus der DB anders geholt.
     * Fuer Institutionen z.B. wird das Gesuch nur mit den relevanten Daten geholt
     */
    public openSchadenfall(schadenfallId: string): IPromise<TSSchadenfall> {
        return this.schadenfallRS.findSchadenfall(schadenfallId)
                .then((response: TSSchadenfall) => {
                    this.setSchadenfall(response);
                    return response;
                });

    }

    public setSchadenfall(schadenfall?: TSSchadenfall): void {
        Raven.setTagsContext({
            schadenfallId: schadenfall ? schadenfall.getFallnummerWithZeros() : undefined,
            schadenfallStatus: schadenfall ? schadenfall.status : undefined,
        });
        this.schadenfall = schadenfall;
    }

    public getSchadenfall(): TSSchadenfall | undefined {
        return this.schadenfall;
    }

    /**
     *
     * @param schadenfallId id die geprueft wird
     * @return {boolean} True wenn der aktuelle Schadenfall die gegebene id hat
     */
    public isSchadenfall(schadenfallId: string): boolean {
        if (this.schadenfall) {
            return this.schadenfall.id === schadenfallId;
        }
        return false;
    }

    public reloadSchadenfall(): IPromise<TSSchadenfall | undefined> {
        if (this.schadenfall) {
            return this.schadenfallRS.findSchadenfall(this.schadenfall.id).then((schadenfallResp: any) => {
                this.setSchadenfall(schadenfallResp);
                return this.schadenfall;
            });
        }
        return this.$q.when(this.schadenfall);
    }

    /**
     * Update das Gesuch
     * @returns {IPromise<TSSchadenfall>}
     */
    public updateSchadenfall(): IPromise<TSSchadenfall> {
        if (this.schadenfall) {
            return this.schadenfallRS.update(this.schadenfall).then((schadResp: TSSchadenfall) => {
                this.setSchadenfall(schadResp);
                return this.getSchadenfall()!;
            });
        } else {
            throw 'ILLEGAL_STATE';
        }
    }

    public saveBankverbindung(schadenfall: TSSchadenfall): IPromise<TSBankverbindung> | undefined {
        if (schadenfall) {
            return this.schadenfallRS.updateBankverbindung(schadenfall).then((response) => {
                // it is necessary to load Schadenfall again so the instance contains the changes in Bankverbindung
                return this.openSchadenfall(schadenfall.id).then(() => {
                    return response;
                });
            });
        }
        return;
    }

    public setUserAsFallVerantwortlicher(user: TSBenutzer | undefined) {
        if (this.schadenfall) {
            console.log('not yet implemented set  verantwortlicher')
        }
    }

    public getFallVerantwortlicher(): TSBenutzer | undefined {
        if (this.schadenfall) {
            console.log('not yet implemented');
            return undefined;
//            return this.schadenfall.verantwortlicher
        }
        return undefined;
    }

    /**
     * Returns true if the Schadenfall has the given status
     * @param status
     * @returns {boolean}
     */
    public isSchadenfallStatus(status: TSSchadenfallStatus): boolean {
        if (this.schadenfall) {
            return this.schadenfall.status === status;
        }
        return false;

    }

    public clearSchadenfall(): void {
        this.setSchadenfall(undefined)
    }

    public saveSchadenfall(schadenfall: TSSchadenfall): IPromise<TSSchadenfall> {
        return this.schadenfallRS.update(schadenfall).then((schadResp: TSSchadenfall) => {
            this.setSchadenfall(schadResp);
            return this.getSchadenfall()!;
        });
    }

    public handleTransition(schadenfall: TSSchadenfall, scope: TSBemerkungScope, begruendungsCode: TSBegruendungsCode | undefined,
                            bemerkung: string, event: TSSchadenfallEvent,
    ): IPromise<TSSchadenfall> {
        return this.schadenfallRS.handleTransition(schadenfall, scope, begruendungsCode, bemerkung, event).then((schadResp: TSSchadenfall) => {
            this.setSchadenfall(schadResp);
            return this.getSchadenfall()!;
        });
    }

    // handleTransition nur mit schadenfallId
    public handleTransitionForId(schadenfallId: string, scope: TSBemerkungScope, begruendungsCode: TSBegruendungsCode | undefined,
                                 bemerkung: string, event: TSSchadenfallEvent,
    ): IPromise<TSSchadenfall> {
        return this.schadenfallRS.handleTransitionForId(schadenfallId, scope, begruendungsCode, bemerkung, event).then((schadResp: TSSchadenfall) => {
            this.setSchadenfall(schadResp);
            return this.getSchadenfall()!;
        });
    }

    public deleteSchaden(schadenfall: TSSchadenfall, schaden: TSSchaden): IPromise<any> {
        return this.schadenRS.deleteSchaden(schadenfall.id, schaden.id).then((resp: any) => {
            let index = DVBernUtil.getIndexOfElementwithID(schaden, schadenfall.schaeden);
            if (index > -1) {
                schadenfall.schaeden.splice(index, 1);
            }
            this.setSchadenfall(schadenfall);
            return this.getSchadenfall()!;
        });
    }

    public saveSchaden(schadenenToSave: TSSchaden): IPromise<TSSchaden> {
        if (this.schadenfall) {
            let schadenenList: Array<TSSchaden>;
            if (this.schadenfall.schaeden == null || this.schadenfall.schaeden === undefined) {
                schadenenList = [];
                this.schadenfall.schaeden = schadenenList;
            } else {
                schadenenList = this.schadenfall.schaeden;
            }
            return this.schadenRS.saveSchaden(this.schadenfall.id, schadenenToSave)
                    .then((storedSchaden: TSSchaden) => {
                        if (!schadenenToSave.isNew()) {   //gespeichertes entity war nicht neu
                            let i: number = DVBernUtil.getIndexOfElementwithID(schadenenToSave, schadenenList);
                            if (i >= 0) {
                                schadenenList[i] = storedSchaden;
                            }
                        } else {
                            schadenenList.push(storedSchaden);  //zur Liste hinzufuegen
                        }
                        return storedSchaden;
                    });
        }
        this.log.error('could not save Schaden because no schadenfall is managed');
        return this.$q.when(schadenenToSave);
    }

    public saveBemerkung(bemerkungToSave: TSAbstractBemerkungEntity, schadenfallId: string): IPromise<TSAbstractBemerkungEntity> {
        if (bemerkungToSave instanceof TSBenutzerBemerkung) {
            if (this.schadenfall && schadenfallId && schadenfallId === this.schadenfall.id) {
                return this.saveInList(schadenfallId, this.schadenfall.benutzerBemerkungen, bemerkungToSave,
                        (schadenfallId: string, bem: TSBenutzerBemerkung) => {
                            return this.bemerkungRS.saveBenutzerBemerkung(schadenfallId, bem);
                        },
                );
            }
        } else if (bemerkungToSave instanceof TSTransitionBemerkung) {
            if (this.schadenfall && schadenfallId && schadenfallId === this.schadenfall.id) {
                return this.saveInList(schadenfallId,
                        this.schadenfall.transitionBemerkungen,
                        bemerkungToSave,
                        (schadenfallId: string, bem: TSTransitionBemerkung) => {
                            return this.bemerkungRS.saveTransitionBemerkung(schadenfallId, bem);
                        },
                );

            }
        }
        this.log.error('could not save Bemerkung because no schadenfall is managed');
        return this.$q.when(bemerkungToSave);
    }

    private saveInList<T extends TSAbstractBemerkungEntity>(
            schadenfallId: string,
            listToStoreIn: Array<T>,
            objectToSave: T,
            saveFunction: (schadenfallId: string, bem: T) => IPromise<T>,
    ): IPromise<T> {
        let benutzerBemerkungen: Array<T>;
        if (listToStoreIn === null || listToStoreIn === undefined) {
            benutzerBemerkungen = [];
            listToStoreIn = benutzerBemerkungen;
        } else {
            benutzerBemerkungen = listToStoreIn;
        }

        return saveFunction(schadenfallId, objectToSave)
                .then((storedObject: T) => {
                    if (!objectToSave.isNew()) {   //gespeichertes entity war nicht neu
                        let i: number = DVBernUtil.getIndexOfElementwithID(objectToSave, benutzerBemerkungen);
                        if (i >= 0) {
                            benutzerBemerkungen[i] = storedObject;
                        }
                    } else {
                        benutzerBemerkungen.push(storedObject);  //zur Liste hinzufuegen
                    }
                    return storedObject;
                });
    }

    public removeBemerkung(bemerkung: TSAbstractBemerkungEntity, schadenfallId: string): IPromise<any> {
        if (bemerkung instanceof TSBenutzerBemerkung) {
            if (this.schadenfall && this.schadenfall.benutzerBemerkungen !== undefined && schadenfallId === this.schadenfall.id) {
                let benutzerBemerkungen = this.schadenfall.benutzerBemerkungen;
                let i: number = DVBernUtil.getIndexOfElementwithID(bemerkung, benutzerBemerkungen);
                if (i >= 0) {
                    if (bemerkung.isNew()) {
                        benutzerBemerkungen.splice(i, 1); //remove element
                        return this.$q.when(undefined);

                    } else {
                        this.bemerkungRS.deleteBenutzerBemerkung(bemerkung.id).then((response: any) => {
                                    benutzerBemerkungen.splice(i, 1); //remove element
                                    return response;
                                },
                        )
                    }
                }
            }
        } else if (bemerkung instanceof TSTransitionBemerkung) {
            if (this.schadenfall && this.schadenfall.transitionBemerkungen !== undefined && schadenfallId === this.schadenfall.id) {
                let transitionBemerkung = this.schadenfall.transitionBemerkungen;
                let i: number = DVBernUtil.getIndexOfElementwithID(bemerkung, transitionBemerkung);
                if (i >= 0) {
                    if (bemerkung.isNew()) {
                        transitionBemerkung.splice(i, 1); //remove element
                        return this.$q.when(undefined);

                    } else {
                        this.bemerkungRS.deleteTransitionBemerkung(bemerkung.id).then((response: any) => {
                                    transitionBemerkung.splice(i, 1); //remove element
                                    return response;
                                },
                        )
                    }
                }
            }
        }
        return this.$q.when(undefined);
    }

    public saveFremdkosten(fremdkostenToSave: TSFremdkosten): IPromise<TSFremdkosten> {
        if (this.schadenfall) {
            let fremdkostenList: Array<TSFremdkosten>;
            if (this.schadenfall.fremdkosten == null || this.schadenfall.fremdkosten === undefined) {
                fremdkostenList = [];
                this.schadenfall.fremdkosten = fremdkostenList;
            } else {
                fremdkostenList = this.schadenfall.fremdkosten;
            }
            return this.fremdkostenRS.saveFremdkosten(this.schadenfall.id, fremdkostenToSave)
                    .then((storedFremdkosten: TSFremdkosten) => {
                        if (!fremdkostenToSave.isNew()) {   //gespeichertes entity war nicht neu
                            let i: number = DVBernUtil.getIndexOfElementwithID(fremdkostenToSave, fremdkostenList);
                            if (i >= 0) {
                                fremdkostenList[i] = storedFremdkosten;
                            }
                        } else {
                            fremdkostenList.push(storedFremdkosten);  //neuen Schadenfall anfuegen
                        }
                        return storedFremdkosten;
                    });
        }
        this.log.error('could not save Fremdkosten because no schadenfall is managed');
        return this.$q.when(fremdkostenToSave);
    }

    public removeFremdkosten(fremdkosten: TSFremdkosten): IPromise<any> {
        if (this.schadenfall && this.schadenfall.fremdkosten !== undefined) {
            let fremdkostenList = this.schadenfall.fremdkosten;
            let i: number = DVBernUtil.getIndexOfElementwithID(fremdkosten, fremdkostenList);
            if (i >= 0) {
                if (fremdkosten.isNew()) {
                    fremdkostenList.splice(i, 1); //remove element
                    return this.$q.when(undefined);

                } else {
                    return this.fremdkostenRS.deleteFremdkosten(fremdkosten).then((response: any) => {
                                fremdkostenList.splice(i, 1); //remove element
                                return response;
                            },
                    )
                }
            }
        }
        return this.$q.when(undefined);
    }

    public saveVorleistung(vorleistungToSave: TSVorleistung): IPromise<TSVorleistung> {
        if (this.schadenfall) {
            let vorleistungList: Array<TSVorleistung>;
            if (this.schadenfall.vorleistungen == null || this.schadenfall.vorleistungen === undefined) {
                vorleistungList = [];
                this.schadenfall.vorleistungen = vorleistungList;
            } else {
                vorleistungList = this.schadenfall.vorleistungen;
            }
            return this.vorleistungenRS.saveVorleistung(this.schadenfall.id, vorleistungToSave)
                    .then((storedVorleistung: TSVorleistung) => {
                        if (!vorleistungToSave.isNew()) {   //gespeichertes entity war nicht neu
                            let i: number = DVBernUtil.getIndexOfElementwithID(vorleistungToSave, vorleistungList);
                            if (i >= 0) {
                                vorleistungList[i] = storedVorleistung;
                            }
                        } else {
                            vorleistungList.push(storedVorleistung);  //zur Liste hinzufuegen
                        }
                        return storedVorleistung;
                    });
        }
        this.log.error('could not save Vorleistung because no schadenfall is managed');
        this.errorService.addMesageAsError('ERROR_UNEXPECTED');
        return this.$q.when(vorleistungToSave);
    }

    public removeVorleistung(vorleistung: TSVorleistung): IPromise<any> {
        if (this.schadenfall && this.schadenfall.vorleistungen !== undefined) {
            let vorleistungList = this.schadenfall.vorleistungen;
            let i: number = DVBernUtil.getIndexOfElementwithID(vorleistung, vorleistungList);
            if (i >= 0) {
                if (vorleistung.isNew()) {
                    vorleistungList.splice(i, 1); //remove element
                    return this.$q.when(undefined);

                } else {
                    return this.vorleistungenRS.deleteVorleistung(vorleistung).then((response: any) => {
                                vorleistungList.splice(i, 1); //remove element
                                return response;
                            },
                    )
                }
            }
        }
        return this.$q.when(undefined);
    }

    public saveEigenleistungen(eigenleistungenToSave: TSEigenleistung): IPromise<TSEigenleistung> {
        if (this.schadenfall) {
            let eigenleistungenList: Array<TSEigenleistung>;
            if (this.schadenfall.eigenleistungen == null || this.schadenfall.eigenleistungen === undefined) {
                eigenleistungenList = [];
                this.schadenfall.eigenleistungen = eigenleistungenList;
            } else {
                eigenleistungenList = this.schadenfall.eigenleistungen;
            }
            return this.eigenleistungenRS.saveEigenleistungen(this.schadenfall.id, eigenleistungenToSave)
                    .then((storedEigenleistungen: TSEigenleistung) => {
                        if (!eigenleistungenToSave.isNew()) {   //gespeichertes entity war nicht neu
                            let i: number = DVBernUtil.getIndexOfElementwithID(eigenleistungenToSave, eigenleistungenList);
                            if (i >= 0) {
                                eigenleistungenList[i] = storedEigenleistungen;
                            }
                        } else {
                            eigenleistungenList.push(storedEigenleistungen);  //zur Liste hinzufuegen
                        }
                        return storedEigenleistungen;
                    });
        }
        this.log.error('could not save Fremdkosten because no schadenfall is managed');
        return this.$q.when(eigenleistungenToSave);
    }

    public removeEigenleistung(eigenleistung: TSEigenleistung): IPromise<any> {
        if (this.schadenfall && this.schadenfall.eigenleistungen !== undefined) {
            let eigenleistungenList = this.schadenfall.eigenleistungen;
            let i: number = DVBernUtil.getIndexOfElementwithID(eigenleistung, eigenleistungenList);
            if (i >= 0) {
                if (eigenleistung.isNew()) {
                    eigenleistungenList.splice(i, 1); //remove element
                    return this.$q.when(undefined);

                } else {
                    return this.eigenleistungenRS.deleteEigenleistung(eigenleistung).then((response: any) => {
                                eigenleistungenList.splice(i, 1); //remove element
                                return response;
                            },
                    )
                }
            }
        }
        return this.$q.when(undefined);
    }

    public saveBeitrag(beitragToSave: TSBeitrag): IPromise<TSBeitrag> {
        if (this.schadenfall) {
            let beitragList: Array<TSBeitrag>;
            if (this.schadenfall.beitraege == null || this.schadenfall.beitraege === undefined) {
                beitragList = [];
                this.schadenfall.beitraege = beitragList;
            } else {
                beitragList = this.schadenfall.beitraege;
            }
            return this.beitragRS.saveSchadenfallBeitrag(this.schadenfall.id, beitragToSave)
                    .then((storedBeitrag: TSBeitrag) => {
                        if (!beitragToSave.isNew()) {   //gespeichertes entity war nicht neu
                            let i: number = DVBernUtil.getIndexOfElementwithID(beitragToSave, beitragList);
                            if (i >= 0) {
                                beitragList[i] = storedBeitrag;
                            }
                        } else {
                            beitragList.push(storedBeitrag);  //zur Liste hinzufuegen
                        }
                        return storedBeitrag;
                    });
        }
        this.log.error('could not save Beitrag because no schadenfall is managed');
        this.errorService.addMesageAsError('ERROR_UNEXPECTED');
        return this.$q.when(beitragToSave);
    }

    public removeBeitrag(beitrag: TSBeitrag): IPromise<any> {
        if (this.schadenfall && this.schadenfall.beitraege !== undefined) {
            let beitragList = this.schadenfall.beitraege;
            let i: number = DVBernUtil.getIndexOfElementwithID(beitrag, beitragList);
            if (i >= 0) {
                if (beitrag.isNew()) {
                    beitragList.splice(i, 1); //remove element
                    return this.$q.when(undefined);

                } else {
                    this.beitragRS.deleteBeitrag(beitrag).then((response: any) => {
                                beitragList.splice(i, 1); //remove element
                                return response;
                            },
                    )
                }
            }
        }
        return this.$q.when(undefined);
    }

    public isSchadenReadonly(pageName: string): boolean {
        let fall = this.schadenfall;
        if (fall && this.authServiceRS.getPrincipalRoles()) {
            let roles = this.authServiceRS.getPrincipalRoles();
            let status = fall.status;
            if (roles === undefined || fall === undefined || status === undefined) {
                return true;
            } else {
                //wir testen alle rollen ob wir bei irgend einer readonly false haben. wenn ja dann sind wir nicht
                // readonly
                return roles.map(value => this.berechtigungsService.isStatusReadonlyForRole(value, status, pageName))
                        .filter(isReadonly => isReadonly === false).length === 0;
            }
        }
        return false;
    }

    public isSchadenVisible(pageName: string): boolean {
        let fall = this.schadenfall;
        if (fall && this.authServiceRS.getPrincipalRoles()) {
            let roles = this.authServiceRS.getPrincipalRoles();
            let status = fall.status;
            if (roles === undefined || fall === undefined || status === undefined) {
                return true;
            } else {
                //wir testen alle rollen ob wir bei irgend einer Access hat. wenn ja dann sind wir visible

                // roles.forEach(value => console.log('visibility of page  ' + pageName + ' for role ' + value,
                //     this.berechtigungsService.isStatusVisibleForRole(value, status, pageName)));
                return roles.map(value => this.berechtigungsService.isStatusVisibleForRole(value, status, pageName))
                        .filter(isVisible => isVisible === true).length !== 0;
            }
        }
        return false;
    }

    public isSchadenVisibleAndNotNew(pageName: string): boolean {
        return this.isSchadenVisible(pageName) && !(this.schadenfall && this.schadenfall.isNew());
    }

    public isStatusAbAuftragSchaetzer(): boolean {
        return this.schadenfall !== undefined && this.schadenfall.status !== TSSchadenfallStatus.NEU &&
                this.schadenfall.status !== TSSchadenfallStatus.NEU_PAPIER_ANLAUFSTELLE &&
                this.schadenfall.status !== TSSchadenfallStatus.NEU_PAPIER_FS &&
                this.schadenfall.status !== TSSchadenfallStatus.IN_BEARBEITUNG_ANLAUFSTELLE &&
                this.schadenfall.status !== TSSchadenfallStatus.IN_BEARBEITUNG_ANLAUFSTELLE_STICHENTSCHEID_FS;

    }

    public isStatusAfterNeu(): boolean {
        return this.schadenfall !== undefined && this.schadenfall.status !== TSSchadenfallStatus.NEU;

    }

    public isStatusNeuPapierFS(): boolean {
        return this.schadenfall !== undefined && this.schadenfall.status === TSSchadenfallStatus.NEU_PAPIER_FS;
    }

    public addSchaetzer(selectedSchaetzer: TSBenutzer): IPromise<void> {
        if (this.schadenfall) {
            return this.schadenfallRS.addSchaetzer(this.schadenfall.id, selectedSchaetzer.id).then(response => {
                if (selectedSchaetzer !== undefined && this.schadenfall) {
                    this.schadenfall.schaetzer = this.schadenfall.schaetzer.concat(selectedSchaetzer);
                }
            });
        }
        return this.$q.reject(undefined);
    }

    public removeSchaetzer(selectedSchaetzer: TSBenutzer): IPromise<void> {
        if (this.schadenfall) {
            let schaetzerList = this.schadenfall.schaetzer;
            let i: number = DVBernUtil.getIndexOfElementwithID(selectedSchaetzer, schaetzerList);
            if (i >= 0) {
                return this.schadenfallRS.removeSchaetzer(this.schadenfall.id, selectedSchaetzer.id).then(response => {
                    schaetzerList.splice(i, 1);
                });
            }
        }
        return this.$q.reject(undefined);
    }

    public updateStatus(selectedStatus: TSSchadenfallStatus): IPromise<TSSchadenfall> {
        if (this.schadenfall) {
            return this.schadenfallRS.updateStatus(this.schadenfall.id, selectedStatus).then((schadResp: TSSchadenfall) => {
                this.setSchadenfall(schadResp);
                return this.getSchadenfall()!;
            });
        }
        return this.$q.reject(undefined);
    }
}



// WEBPACK FOOTER //
// ./src/app/core/service/SchadenfallModelManager.ts